import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { checkPromotionAvailability, submitPromotion, PromotionAvailability } from '../services/api';
import toast from 'react-hot-toast';
import { LandingHeader } from '../components/LandingHeader';
import { Footer } from '../components/Footer';

const FreeSubscription = () => {
    const navigate = useNavigate();
    const [randomCaption, setRandomCaption] = useState('');
    const [copyButtonText, setCopyButtonText] = useState('Copy Text');
    const [copyButtonBg, setCopyButtonBg] = useState('bg-transparent');
    const [promotionStatus, setPromotionStatus] = useState<PromotionAvailability | null>(null);
    const [postUrls, setPostUrls] = useState<string[]>(['']);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [promoImage, setPromoImage] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const captions = [
        `📌 Managing my Instagram & X saves just got smarter with {brand_name}!
✨ Auto-categorization
✨ Easy search
✨ Clean saves management

Loving how simple it is to keep everything organized 🙌

#Instavault #Productivity #OrganizedLife`,

        `🚀 No more endless scrolling to find my saved posts!
Using {brand_name} to manage all my Instagram & X saves in one place.
🔍 Search any save instantly
📂 Auto-categorize content
🗂️ Stress-free saves management

Game changer 💜

#Instavault #SmartSaves #AppRecommendation`,

        `✨ Finally, a way to organize my Instagram & X saves without the mess!
Instavault = search + auto-categorization + easy saves management 🙌
{brand_name}

#Instavault #OrganizedSaves #Productivity`,

        `📂 My saved posts were chaos — until I found {brand_name}.
Now I can search, auto-sort, and manage everything in seconds 🚀

#Instavault #GameChanger #SmartSaves`,

        `💡 From endless scrolling to instant access!
{brand_name} makes my Instagram & X saves searchable and organized 🌟

#Instavault #ProductivityTools #LifeMadeEasy`,

        `🚀 Can't believe I was managing saves without {brand_name} before!
It auto-categorizes and makes finding posts super easy 🔍

#Instavault #AppLife #OrganizedLiving`,

        `🔖 One app. All my saves. Total peace of mind.
Thanks to {brand_name}, I can finally manage Instagram & X saves stress-free.

#Instavault #SmartSaves #AppRecommendation`
    ];

    useEffect(() => {
        // Select random caption and replace Instavault with @instavaultco
        const randomIndex = Math.floor(Math.random() * captions.length);
        const selectedCaption = captions[randomIndex].replace("{brand_name}", '@instavaultco');
        setRandomCaption(selectedCaption);
    }, []);

    useEffect(() => {
        // Set random promo image once when component mounts
        const randomImage = Math.random() < 0.5 ? "/assets/post1.jpeg" : "/assets/post2.jpeg";
        setPromoImage(randomImage);
    }, []);

  useEffect(() => {
    // Check promotion status
    const checkPromotion = async () => {
      try {
        setIsLoading(true);
        const status = await checkPromotionAvailability();
        setPromotionStatus(status);
      } catch (error) {
        console.error('Error checking promotion availability:', error);
        // Set default state on error to prevent undefined state
        setPromotionStatus({ is_offer_claimed: false, is_promotion_credited: false });
      } finally {
        setIsLoading(false);
      }
    };

    checkPromotion();
  }, []);

    const handlePromotionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Filter out empty URLs and validate
        const validUrls = postUrls.filter(url => url.trim());

        if (validUrls.length === 0) {
            toast.error('Please enter at least one valid post URL');
            return;
        }

        // Validate each URL
        const urlPattern = /^https?:\/\/.+/;
        const invalidUrls = validUrls.filter(url => !urlPattern.test(url));
        if (invalidUrls.length > 0) {
            toast.error('Please enter valid URLs (must start with http:// or https://)');
            return;
        }

        setIsSubmitting(true);
        try {
            await submitPromotion(validUrls);
            toast.success('Thanks for posting. We will check the details and credit your 1 month free subscription within 7 days');
            // Update promotion status to claimed but not credited
            setPromotionStatus({ is_offer_claimed: true, is_promotion_credited: false });
            setPostUrls(['']);
        } catch (error) {
            console.error('Error submitting promotion:', error);
            // Handle different types of errors
            if (error instanceof Error) {
                toast.error(`Failed to submit promotion: ${error.message}`);
            } else {
                toast.error('Failed to submit promotion. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const addPostUrl = () => {
        setPostUrls([...postUrls, '']);
    };

    const removePostUrl = (index: number) => {
        if (postUrls.length > 1) {
            setPostUrls(postUrls.filter((_, i) => i !== index));
        }
    };

    const updatePostUrl = (index: number, value: string) => {
        const newUrls = [...postUrls];
        newUrls[index] = value;
        setPostUrls(newUrls);
    };

    const handleCopyText = async () => {
        try {
            await navigator.clipboard.writeText(randomCaption);
            setCopyButtonText('✓ Text copied');
            setCopyButtonBg('bg-green-600');

            // Reset button after 2 seconds
            setTimeout(() => {
                setCopyButtonText('Copy Text');
                setCopyButtonBg('bg-transparent');
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    // Show loading state while checking promotion status
    if (isLoading || !promotionStatus) {
        return (
            <div className="min-h-screen bg-gray-900 flex flex-col">
                <LandingHeader showLoginButton={false} />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center text-white">
                        <h1 className="text-2xl font-bold mb-4">Loading...</h1>
                        <p className="text-gray-400">Checking promotion status...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Show message if offer is already claimed and credited
    if (promotionStatus && promotionStatus.is_offer_claimed && promotionStatus.is_promotion_credited) {
        return (
            <div className="min-h-screen bg-gray-900 flex flex-col">
                <LandingHeader showLoginButton={false} />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center text-white max-w-md">
                        <div className="bg-green-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold mb-4">🎉 Congratulations!</h1>
                        <p className="text-gray-400 mb-6">
                            You have already claimed this offer and received your free subscription for one month. 
                            Enjoy your premium features!
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
                        >
                            Go Back to Home
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Show message if offer is claimed but not yet credited
    if (promotionStatus && promotionStatus.is_offer_claimed && !promotionStatus.is_promotion_credited) {
        return (
            <div className="min-h-screen bg-gray-900 flex flex-col">
                <LandingHeader showLoginButton={false} />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center text-white max-w-md">
                        <div className="bg-yellow-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold mb-4">⏳ Under Review</h1>
                        <p className="text-gray-400 mb-6">
                            You have already claimed this offer. We are reviewing your submission and will credit your 
                            free subscription within 7 days. Thank you for your patience!
                        </p>
                        <button
                            onClick={() => navigate('/')}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
                        >
                            Go Back to Home
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Only show the form if offer is not claimed (available to claim)
    return (
        <div className="min-h-screen bg-gray-900 flex flex-col">
            <LandingHeader showLoginButton={false} />
            <div className="flex-1">
                <div className="max-w-4xl mx-auto p-6">
                    {/* Clean header with confetti icons */}
                    <div className="mb-8 text-center">
                        <div className="flex items-center justify-center gap-4 mb-4">
                            <span className="text-2xl">🎉</span>
                            <h1 className="text-3xl font-bold text-white">Get 1 Month Free Subscription!</h1>
                            <span className="text-2xl">🎉</span>
                        </div>
                        <p className="text-gray-400">Post us on your Instagram or X and get a month's subscription free!</p>
                    </div>

                    <div className="space-y-6">
                        {/* Step 1: Download Image */}
                        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 shadow-lg shadow-gray-900/50">
                            <div className="flex items-center mb-4">
                                <div className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold mr-4">
                                    1
                                </div>
                                <h2 className="text-xl font-semibold text-white">Download this image</h2>
                            </div>
                            <div className="flex justify-center">
                                <div className="relative group">
                                    <img
                                        src={promoImage}
                                        alt="InstaVault Promotional Image"
                                        className="w-64 h-80 bg-white rounded-lg object-cover object-center"
                                    />
                                    <a
                                        href={promoImage}
                                        download
                                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/50 rounded-lg"
                                    >
                                        <div className="bg-white/20 rounded-full p-3 backdrop-blur-sm">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-6 h-6 text-white"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                                            </svg>
                                        </div>
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Step 2: Copy Text */}
                        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 shadow-lg shadow-gray-900/50">
                            <div className="flex items-center mb-4">
                                <div className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold mr-4">
                                    2
                                </div>
                                <h2 className="text-xl font-semibold text-white">Copy this text</h2>
                            </div>
                            
                            <div className="bg-gray-700/50 rounded-lg p-4 mb-4 relative">
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-lg"></div>
                                <p className="text-gray-300 text-sm whitespace-pre-line pl-4">
                                    {randomCaption}
                                </p>
                            </div>
                            
                            <button
                                onClick={handleCopyText}
                                className={`${copyButtonBg === 'bg-green-600' ? 'bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} text-white px-6 py-2 rounded-lg transition-colors`}
                            >
                                {copyButtonText}
                            </button>
                        </div>

                        {/* Step 3: Post Instructions */}
                        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 shadow-lg shadow-gray-900/50">
                            <div className="flex items-center mb-4">
                                <div className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold mr-4">
                                    3
                                </div>
                                <h2 className="text-xl font-semibold text-white">Post on Instagram or X</h2>
                            </div>

                            <p className="text-gray-300 text-sm">
                                Post the downloaded image and caption on Instagram, X or both.
                            </p>
                        </div>

                        {/* Step 4: Submit Form */}
                        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 shadow-lg shadow-gray-900/50">
                            <div className="flex items-center mb-4">
                                <div className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold mr-4">
                                    4
                                </div>
                                <h2 className="text-xl font-semibold text-white">Submit your post link</h2>
                            </div>
                            
                            <p className="text-gray-300 text-sm mb-6">
                                Enter the URL(s) of your post(s) and submit. You can add multiple posts if you've shared on both Instagram and X. We will extend your free trial by one month, subject to our <a href="https://instavault.co/offer-terms" target="_blank" rel="noopener noreferrer" className="underline text-blue-400 hover:text-blue-300">offer terms and conditions</a>.
                            </p>
                            
                            <div className="space-y-4">
                                <form onSubmit={handlePromotionSubmit} className="space-y-4">
                                    <div className="space-y-3">
                                        {postUrls.map((url, index) => (
                                            <div key={index} className="flex gap-2">
                                                <input
                                                    type="url"
                                                    placeholder="Link to your post"
                                                    className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:border-blue-500 focus:outline-none placeholder-gray-400"
                                                    pattern="https?://.+"
                                                    title="Please enter a valid URL (must start with http:// or https://)"
                                                    value={url}
                                                    onChange={(e) => updatePostUrl(index, e.target.value)}
                                                />
                                                {postUrls.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removePostUrl(index)}
                                                        className="px-4 py-3 bg-gray-600 hover:bg-red-700/20 text-white rounded-lg transition-colors"
                                                        title="Remove this URL"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={addPostUrl}
                                        className="w-full bg-transparent border-2 border-dashed border-gray-500 text-gray-300 hover:text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                                    >
                                        <span className="text-lg">+</span>
                                        Add another post
                                    </button>

                                    <button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-blue-400 to-purple-600 hover:from-blue-400/20 hover:to-purple-600/20 text-white px-6 py-3 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-semibold relative overflow-hidden"
                                        disabled={isSubmitting}
                                    >
                                        <span className="relative z-10 flex items-center justify-center gap-2">
                                            {isSubmitting ? 'Submitting...' : 'Get one month free subscription'}
                                            <span className="text-lg">✨</span>
                                        </span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 animate-pulse"></div>
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default FreeSubscription;
